import React from 'react';
import cx from 'clsx';
import styles from './GameDetailsTop.module.scss';
import { AccountLi } from '../icons';
import { CallbackMode } from '../../logic/main/types';
import { GameTileProps, GameClickProps } from '../modular-grid/types';
import AssetLayer from '../AssetLayer';
import { LayersWrapper } from '../game-tile';
import { mapUrl } from '../../logic/main/utils';
import { mapTable } from '../../model/algolia/utils/index';
import { DeviceContext } from '../DeviceProvider/DeviceProvider';
import { GameMode } from 'src/types/gameTypes';

export interface GameDetailsTopMessages {
  features: string;
  tableInfo: string;
  playButton: string;
  practiceButton?: string;
}
interface GameDetailsTopPropTypes {
  game: GameTileProps;
  callbackMode: CallbackMode;
  onGameButtonClick: ({
    game,
    gamePosition,
    sectionTitle,
    gameMode,
  }: {
    game: GameClickProps;
    gamePosition: number;
    sectionTitle: string;
    gameMode?: GameMode;
  }) => void;
  staticTexts: GameDetailsTopMessages;
}

const GameDetailsTop: React.FC<GameDetailsTopPropTypes> = ({
  game,
  callbackMode,
  onGameButtonClick,
  staticTexts,
}) => {
  let layerInfoText = '';
  const { deviceType, deviceOs } = React.useContext(DeviceContext);
  if (game.table && mapTable(game.table)) {
    const { stake, playersInfo } = mapTable(game.table)!;
    layerInfoText = stake ? `${playersInfo}, ${stake}` : playersInfo;
  }
  const parsedGameFeatures = game.features && game.features.join(', ');

  return (
    <div
      data-testid="game-details-header"
      className={cx(
        game.infoPageImages ? styles.headerWrapper : styles.headerWrapperSmall
      )}
    >
      <div
        className={cx(
          styles.headerContent,
          !game.infoPageImages && styles.headerContentSimple
        )}
      >
        {game.infoPageImages ? (
          <>
            <div
              data-testid="game-details-header-image"
              className={cx(styles.image, styles.imageBig)}
            >
              <div
                data-testid="game-details-header-image-overlay"
                className={cx(styles.overlay)}
              >
                <LayersWrapper>
                  <AssetLayer
                    type="image"
                    sizedSources={game.infoPageImages.background}
                    rounded="none"
                    alt={game.title}
                  />
                </LayersWrapper>
              </div>
              <img
                data-testid="game-details-header-logo"
                alt="logo"
                className={styles.logo}
                src={
                  game.infoPageImages.logo.source.retina ||
                  game.infoPageImages.logo.source.normal
                }
              />
            </div>
          </>
        ) : (
          <div
            data-testid="game-details-header-image-simple"
            className={cx(styles.image, styles.imageSmall)}
          >
            <img
              data-testid="game-details-header-image-simple-tag"
              alt={game.title}
              className={styles.imageTag}
              src={game.image.source.normal}
            />
          </div>
        )}
        <div
          className={cx(
            game.infoPageImages
              ? styles.gameInfoWrapper
              : styles.gameInfoWrapperSimple
          )}
        >
          <div className={cx(styles.gameInfoDetails)}>
            <p
              data-testid="game-details-header-game-type"
              className={cx(
                styles.type,
                !game.infoPageImages && styles.typeFallbackColor
              )}
            >
              {game.type}
            </p>
            <p
              data-testid="game-details-header-game-title"
              className={cx(
                styles.title,
                game.infoPageImages ? styles.titleBig : styles.titleSmall
              )}
            >
              {game.title}
            </p>
            {game.infoPageImages && game.features && (
              <div
                data-testid="game-details-header-game-features"
                className={cx(
                  styles.gameFeatures,
                  !game.infoPageImages && styles.gameFeaturesFallbackColor
                )}
              >
                <p className={styles.gameFeaturesTitle}>
                  {staticTexts.features}: {parsedGameFeatures}
                </p>
              </div>
            )}
            {game.table && (
              <div
                data-testid="game-details-header-game-table-info"
                className={styles.gameTableInfo}
              >
                <span>{staticTexts.tableInfo}: </span>
                <AccountLi className={styles.tableIcon} />
                <span className={styles.tableInfo}>{layerInfoText}</span>
              </div>
            )}
          </div>
          <div className={cx(styles.linkWrapper)}>
            <a
              className={cx(styles.link, styles.linkSolid, styles.headerLink)}
              data-testid="game-details-header-play-button"
              target="_blank"
              rel={`noopener noreferrer ${game.notIndexed ? 'nofollow' : ''}`}
              href={mapUrl(game.launchUrl, deviceType, deviceOs)}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                if (callbackMode === 'event') e.preventDefault();
                onGameButtonClick &&
                  onGameButtonClick({
                    game: game,
                    gamePosition: 1,
                    sectionTitle: 'Game Details',
                  });
              }}
            >
              {staticTexts.playButton}
            </a>
            {game.practiceUrl && (
              <a
                className={cx(
                  styles.link,
                  styles.linkHollow,
                  styles.headerLink
                )}
                data-testid="game-details-header-practice-button"
                target="_blank"
                rel={`noopener noreferrer ${game.notIndexed ? 'nofollow' : ''}`}
                href={mapUrl(game.practiceUrl, deviceType, deviceOs)}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (callbackMode === 'event') e.preventDefault();
                  onGameButtonClick &&
                    onGameButtonClick({
                      game: game,
                      gamePosition: 1,
                      sectionTitle: 'Game Details',
                      gameMode: GameMode.Practice,
                    });
                }}
              >
                {staticTexts.practiceButton}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(GameDetailsTop);
